// Windowed masonry. Sidecar w/h means the ENTIRE layout (x, y, w, h per tile plus
// total height) computes in one cheap pass — so only tiles near the viewport are
// mounted (~30-60 nodes) no matter whether the category holds 50 or 5000 items.
// No per-tile IntersectionObservers, no grid video autoplay (posters + play badge;
// playback lives in the lightbox) — that's what keeps thousands of items smooth.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { thumbUrl } from '../api.js';
import { GALLERY } from '../text.js';
import { CheckIcon, PlayIcon } from './icons.jsx';

const GAP = 12;
const OVERSCAN = 1200; // px above/below the viewport kept mounted
const SCROLL_QUANTUM = 300; // re-render only every N px of scroll

function columnsFor(width) {
	if (width < 640) return 2;
	if (width < 1100) return 3;
	return 4;
}

function computeLayout(items, width) {
	const cols = columnsFor(width);
	const colW = (width - GAP * (cols - 1)) / cols;
	const heights = new Array(cols).fill(0);
	const tiles = items.map((item, index) => {
		const ratio = item.w && item.h ? item.w / item.h : 3 / 4;
		const h = colW / ratio;
		let c = 0;
		for (let i = 1; i < cols; i++) {
			if (heights[i] < heights[c]) c = i;
		}
		const tile = { item, index, x: c * (colW + GAP), y: heights[c], w: colW, h };
		heights[c] += h + GAP;
		return tile;
	});
	return { tiles, height: Math.max(0, ...heights) - GAP };
}

function Tile({ onOpen, onToggleSelect, selectMode, selected, tile }) {
	const { item } = tile;
	const style = {
		transform: `translate(${tile.x}px, ${tile.y}px)`,
		width: tile.w,
		height: tile.h,
	};

	if (item.status === 'processing') {
		return (
			<div className='mtile mtile--processing' style={style} aria-label={GALLERY.processing}>
				<span className='mtile__spinner' aria-hidden='true' />
				<span className='mtile__processing-label'>{GALLERY.processing}</span>
			</div>
		);
	}

	const onClick = () => {
		if (selectMode) onToggleSelect(item.id);
		else onOpen(tile.index);
	};

	return (
		<button
			type='button'
			className={`mtile ${selected ? 'mtile--selected' : ''}`}
			style={style}
			onClick={onClick}
			aria-label={item.type === 'video' ? `Video ${tile.index + 1}` : `Foto ${tile.index + 1}`}
			aria-pressed={selectMode ? selected : undefined}>
			<img className='mtile__img' src={thumbUrl(item)} alt='' loading='lazy' decoding='async' />
			{item.type === 'video' && (
				<span className='mtile__play' aria-hidden='true'>
					<PlayIcon size={22} />
				</span>
			)}
			{selectMode && (
				<span className={`mtile__check ${selected ? 'mtile__check--on' : ''}`} aria-hidden='true'>
					<CheckIcon size={14} />
				</span>
			)}
		</button>
	);
}

export default function Masonry({ items, onOpen, onToggleSelect, selectMode, selectedIds }) {
	const containerRef = useRef(null);
	const [width, setWidth] = useState(0);
	const [range, setRange] = useState({ min: 0, max: 2000 });

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver((entries) => {
			setWidth(entries[0].contentRect.width);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const layout = useMemo(() => (width > 0 ? computeLayout(items, width) : null), [items, width]);

	useEffect(() => {
		let ticking = false;
		let lastMin = -1;
		const update = () => {
			ticking = false;
			const el = containerRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			// Quantize so scrolling re-renders only every SCROLL_QUANTUM px, not per frame.
			const min = Math.floor((-rect.top - OVERSCAN) / SCROLL_QUANTUM) * SCROLL_QUANTUM;
			if (min === lastMin) return;
			lastMin = min;
			setRange({ min, max: min + window.innerHeight + 2 * OVERSCAN + SCROLL_QUANTUM });
		};
		const onScroll = () => {
			if (!ticking) {
				ticking = true;
				window.requestAnimationFrame(update);
			}
		};
		update();
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, []);

	const visible = layout ? layout.tiles.filter((t) => t.y + t.h > range.min && t.y < range.max) : [];

	return (
		<div ref={containerRef} className='masonry' style={{ height: layout ? layout.height : undefined }}>
			{visible.map((tile) => (
				<Tile
					key={tile.item.id}
					tile={tile}
					selectMode={selectMode}
					selected={!!selectedIds?.has(tile.item.id)}
					onToggleSelect={onToggleSelect}
					onOpen={onOpen}
				/>
			))}
		</div>
	);
}
